import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from './router';
import { Toast } from './components/ui/Toast';
import './index.css';
import { useEffect, useState } from 'react';
import { useAuthStore } from './store/authStore';
import { useCompanyStore } from './store/companyStore';
import { useThemeStore } from './store/themeStore';
import { apiGet } from './api/http';

function App() {
  const { setUser, logout } = useAuthStore();
  const { setCompany } = useCompanyStore();
  const [loading, setLoading] = useState(true);
  
  // Inicializa a escuta de temas globais
  useThemeStore();

  useEffect(() => {
    const applySession = (res: any) => {
      if (res?.company) {
        setCompany({
          id: String(res.company.id),
          name: res.company.name,
          document: res.company.document || '',
          email: res.company.email || '',
          phone: res.company.phone || '',
          address: `${res.company.street || ''}, ${res.company.number || ''} ${res.company.complement || ''} - ${res.company.city || ''}, ${res.company.state || ''}`,
          logo: res.company.logo || '/company_logo_placeholder.png',
          settings: typeof res.company.settings === 'string' ? JSON.parse(res.company.settings) : (res.company.settings || {}),
          subscription: res.company.subscription || {
            plan: 'Nenhum',
            status: 'active',
            expiresAt: null,
            features: [],
          }
        });
      }
      if (res?.user) {
        setUser({
          id: String(res.user.id),
          name: res.user.name,
          email: res.user.email,
          role: res.user.role,
          is_owner: res.user.is_owner || 0,
          is_superadmin: res.user.is_superadmin || 0,
          lock_timeout_minutes: res.user.lock_timeout_minutes || 0,
          profile_name: res.user.profile_name || '',
          permissions: res.user.permissions || [],
        });
      }
    };

    // Refetch silencioso: mantém plano/features/permissões em dia para uma aba já
    // aberta, já que um admin pode alterar o plano/módulos da empresa em outra sessão
    // enquanto o usuário permanece logado (sem F5) na mesma.
    // Representante/vendedor (Portal Comercial): sessão vem de /portal/me, sem empresa.
    const isPortalAccount = () => ['partner', 'agent'].includes(localStorage.getItem('account_type') || '');
    const applyPortalSession = async () => {
      try {
        const res = await apiGet('/portal/me');
        setUser({
          id: String(res.user.id),
          name: res.user.name,
          email: res.user.email,
          role: 'user',
          is_owner: 0,
          is_superadmin: 0,
          account_type: res.user.account_type,
          lock_timeout_minutes: 0,
          profile_name: res.user.account_type === 'partner' ? 'Representante' : 'Vendedor',
          permissions: [],
        });
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) logout();
      }
    };

    const refreshSession = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      if (isPortalAccount()) {
        await applyPortalSession();
        return;
      }
      // Já sabemos que vai dar 402 aqui -- evita ficar martelando a API e
      // sujando o console a cada 3min enquanto a pessoa resolve o pagamento.
      if (window.location.pathname === '/subscription/plans') return;
      try {
        const res = await apiGet('/companies/my-company');
        applySession(res);
      } catch (err: any) {
        if (err?.status === 401) logout();
      }
    };

    const recoverSession = async () => {
      const token = localStorage.getItem('token');
      if (token && isPortalAccount()) {
        await applyPortalSession();
      } else if (token) {
        try {
          const res = await apiGet('/companies/my-company');
          applySession(res);
        } catch (err: any) {
          console.error('Failed to recover session:', err);
          // Erro de rede/servidor: a tela mostra "não foi possível carregar" com nova tentativa,
          // em vez de tratar o usuário como sem permissão
          if (err?.status !== 401 && err?.status !== 402) useAuthStore.getState().setSessionError(true);
          // 402 (assinatura pendente/expirada) já redireciona pra
          // /subscription/plans dentro do próprio apiGet -- deslogar aqui
          // apagaria o token no meio do redirecionamento e derrubaria a
          // sessão que a pessoa precisa pra confirmar o plano.
          if (err?.status === 401) logout();
        }

        // Termos/Contrato pendentes de aceite (ex.: nova versão publicada,
        // ou conta antiga que ainda não passou por essa tela) bloqueiam o
        // acesso independente do resultado acima -- por isso não usa
        // requireTenant no backend e roda mesmo com assinatura 'unpaid'.
        if (window.location.pathname !== '/aceite-termos') {
          try {
            const pendingRes = await apiGet('/legal/pending');
            if (pendingRes?.pending?.length) {
              sessionStorage.setItem('post_legal_redirect', window.location.pathname);
              window.location.href = '/aceite-termos';
              return;
            }
          } catch {
            // Falha na checagem não deve travar o acesso.
          }
        }
      }
      setLoading(false);
    };

    recoverSession();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshSession();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', refreshSession);
    const interval = setInterval(refreshSession, 3 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', refreshSession);
      clearInterval(interval);
    };
  }, [setUser, setCompany, logout]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <BrowserRouter basename={__BASE_PATH__}>
      <AppRoutes />
      <Toast />
    </BrowserRouter>
  );
}

export default App;
